// Shared logic for the transaction-based change flows (Course Change / Level Change /
// Course Transfer). All three start by selecting a paid transaction, then act on that
// transaction's REMAINING classes. "Remaining" = unscheduled + scheduled classes for the
// course (from the /admin/courses studentCourses response) — both are eligible to move.
import { toast } from 'react-toastify';
import {
  getAllPayments,
  getExternalPayment,
  getRefundList,
  studentCourses,
  getCoursePrice,
} from '../../../../../Api/apiUtils';

// Spread onto every react-select rendered inside the change popup so its dropdown menu is
// portaled to <body> and not clipped by the Modal's scrollable overflow.
export const selectMenuPortalProps: any = {
  menuPortalTarget: typeof document !== 'undefined' ? document.body : undefined,
  menuPosition: 'fixed',
  styles: { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) },
};

export const isInstrumentComboPayment = (payment: any) =>
  payment?.instrumentCombo === true || payment?.instrumentCombo?.enabled === true;

// Fold wallet / special / additional / flash discounts into each course's discountedPrice,
// exactly as the refund flow (transactionList.tsx) does, so the remaining value matches.
export const applyOtherDiscounts = (transaction: any) => {
  let otherDiscountAmount = 0;
  if (transaction.myCash || transaction.artiumCash || transaction.referralCode) {
    otherDiscountAmount += (+transaction.artiumCash || 0) + (+transaction.myCash || 0);
  }
  if (transaction.specialDiscount || transaction.additionalDiscount || transaction.flashDiscount) {
    otherDiscountAmount += (+transaction.specialDiscount || 0) + (+transaction.additionalDiscount || 0) + (+transaction.flashDiscount || 0);
  }
  if (otherDiscountAmount) {
    const totalPriceWithoutDiscount = (transaction.courseList || [])
      .filter((e: any) => !e.isFree)
      .reduce((acc: number, course: any) => acc + (+course.unit_price * +course.classesToPurchase), 0);
    if (totalPriceWithoutDiscount > 0) {
      const otherDiscountPercentage = (otherDiscountAmount / totalPriceWithoutDiscount) * 100;
      (transaction.courseList || []).filter((e: any) => !e.isFree).forEach((course: any) => {
        course.discountedPrice = +course.discountedPrice || 0;
        course.discountedPrice += (+(+course.unit_price * +course.classesToPurchase) * otherDiscountPercentage / 100);
      });
    }
  }
};

/**
 * Course-change "remaining classes" = the course's UNSCHEDULED + SCHEDULED classes
 * (from studentCourses). Both are eligible to move to a new course/level/student.
 * The pool is distributed across that course's transactions (newest purchase first) and,
 * within a transaction, to the higher purchased level first, so the total across
 * transactions never exceeds what is actually remaining. Populates, per course:
 *   eligibleRefundClasses (remaining classes) and refundableAmount (remaining value).
 */
export const buildUnscheduledLookup = (studentCoursesData: any[]) => {
  const byCourseId: Record<string, number> = {};
  const nameToCourseId: Record<string, string> = {};
  (studentCoursesData || []).forEach((c: any) => {
    if (c?.courseId != null) {
      const cid = String(c.courseId);
      // Remaining (changeable) classes = unscheduled + scheduled. Accumulate across
      // duplicate course rows (a course can appear once per enrollment).
      const remaining = (+c.unscheduledClasses || 0) + (+c.scheduledClasses || 0);
      byCourseId[cid] = (byCourseId[cid] || 0) + remaining;
      if (c.courseName) nameToCourseId[String(c.courseName).trim().toLowerCase()] = cid;
    }
  });
  return { byCourseId, nameToCourseId };
};

// Numeric rank of a purchased level (higher = more advanced). Parses the digits in the
// level label, falling back to levelId, so the remaining pool fills higher levels first.
export const levelRank = (course: any) => {
  const m = String(course.level ?? '').match(/\d+/);
  if (m) return +m[0];
  return +course.levelId || 0;
};

// Resolve a transaction course to the studentCourses key (courseId, else by course name).
export const resolveCourseKey = (course: any, lookup: any) => {
  const cid = String(course.courseId);
  if (lookup.byCourseId[cid] != null) return cid;
  const name = String(course.courseName || '').trim().toLowerCase();
  return lookup.nameToCourseId[name] || cid;
};

// Exact creation time of a transaction, used to order purchases made on the same invoice date
// (invoice_date has no time). Falls back to the timestamp in an external id (`studentId_<ms>`).
const createdTime = (t: any) => {
  const created = new Date(t.createdAt || 0).getTime();
  if (created) return created;
  const idMs = +(String(t.id || '').split('_').pop() || 0);
  return idMs > 1e12 ? idMs : 0;
};

export const applyRemainingClasses = (transactions: any[], lookup: any) => {
  const pool: Record<string, number> = { ...lookup.byCourseId };
  // Newest purchase first so the latest transaction holds the remaining classes; same-day
  // purchases are ordered by their creation time.
  const sorted = [...transactions].sort(
    (a, b) =>
      new Date(b.invoice_date || b.createdAt || 0).getTime() - new Date(a.invoice_date || a.createdAt || 0).getTime() ||
      createdTime(b) - createdTime(a)
  );
  sorted.forEach((t) => {
    // Within a transaction, fill the higher purchased level first (classes are consumed
    // from lower levels up, so the remaining ones sit in the higher levels).
    const allocationOrder = [...(t.courseList || [])].sort((a, b) => levelRank(b) - levelRank(a));
    allocationOrder.forEach((course: any) => {
      if (isInstrumentComboPayment(t) || course.isFree) {
        course.eligibleRefundClasses = 0;
        course.refundDiscout = 0;
        course.refundableAmount = 0;
        return;
      }
      const key = resolveCourseKey(course, lookup);
      const available = pool[key] != null ? pool[key] : 0;
      const alloc = Math.min(available, +course.classesToPurchase || 0);
      pool[key] = Math.max(0, available - alloc);
      const discountPerClass = +course.discountedPrice ? +course.discountedPrice / +course.classesToPurchase : 0;
      course.eligibleRefundClasses = alloc;
      course.refundDiscout = +(discountPerClass * alloc) || 0;
      course.refundableAmount = (alloc * course.unit_price) - +course.refundDiscout || 0;
    });
  });
};

export const sumRemainingClasses = (t: any) =>
  (t.courseList || []).reduce((acc: number, c: any) => acc + (+c.eligibleRefundClasses || 0), 0);
export const sumRemainingValue = (t: any) =>
  (t.courseList || []).reduce((acc: number, c: any) => acc + (+c.refundableAmount || 0), 0);

export const calculateTotalPrice = (toCourseData: any) => {
  const plan = JSON.parse(JSON.stringify(toCourseData));
  const levelList = plan.levelList;
  plan.classCount = levelList.reduce((acc: number, level: any) => acc + +level.classCount, 0);
  plan.total = Math.max(+levelList.reduce((acc: number, level: any) => acc + +(level.classCount * level.unit_price).toFixed(2), 0), 0);
  plan.discountedTotal = Math.max(+(plan.total - (plan.total * +((plan.discountPercentage) / 100))).toFixed(2), 0);
  return plan;
};

// Load every paid transaction for a student and annotate each course with its remaining
// (changeable) classes and remaining value. Shared by all three transaction-based flows.
export const loadTransactions = async (studentId: string) => {
  const [payments, externalPayments, refundRequests, coursesData] = await Promise.all([
    getAllPayments(studentId),
    getExternalPayment(studentId),
    getRefundList(studentId),
    studentCourses(studentId),
  ]);
  const unscheduledLookup = buildUnscheduledLookup(
    Array.isArray(coursesData) ? coursesData : (coursesData?.data || [])
  );
  // A transaction with a refund request (pending or done) can't also be course-changed —
  // same exclusion as the refund screen (transactionList.tsx).
  const refundRequestIds: string[] = refundRequests?.data?.Items?.map((v: any) => v.id) || [];
  const isRefunded = (e: any) => refundRequestIds.includes(e.id) || (!!e.order_id && refundRequestIds.includes(e.order_id));

  const orders = (payments || [])
    .filter((e: any) =>
      (e.payment_status?.includes('payment_success') || e.webhook_init) &&
      e.courseList &&
      e.transactionStatus !== 'COURSE_CHANGED' &&
      !isRefunded(e) &&
      !isInstrumentComboPayment(e)
    )
    .map((e: any) => ({ ...e, __source: 'order' }));

  const externals = (externalPayments || [])
    .filter((e: any) =>
      (!e.isFreeClass || !e.isFree) &&
      e.transactionStatus !== 'COURSE_CHANGED' &&
      !isRefunded(e)
    )
    .map((e: any) => ({ ...e, __source: 'external' }));

  const processed = [...orders, ...externals].map((t) => ({
    ...t,
    captureDate: t.captureDate || new Date().toISOString().split('T')[0],
  }));
  processed.forEach((t) => applyOtherDiscounts(t));
  applyRemainingClasses(processed, unscheduledLookup);
  return processed;
};

// Most classes of one level that still fit in the available amount, given the classes
// already entered for the OTHER levels (discount-free unit price, as on the change forms).
export const maxPurchasableClasses = (toCourseDetail: any, levelId: string, availableAmount: number) => {
  const level = (toCourseDetail?.levelList || []).find((l: any) => l.levelId === levelId);
  if (!level || !(+level.unit_price > 0)) return 0;
  const otherLevelsValue = toCourseDetail.levelList
    .filter((l: any) => l.levelId !== levelId)
    .reduce((acc: number, l: any) => acc + (+l.classCount || 0) * +l.unit_price, 0);
  return Math.max(0, Math.floor((availableAmount - otherLevelsValue + 0.5) / +level.unit_price));
};

// The active level of the new course: the first level, in the course's level order
// (Preparatory → Advanced), that has classes entered.
export const firstFilledLevelId = (toCourseDetail: any): string | null =>
  (toCourseDetail?.levelList || []).find((l: any) => +l.classCount > 0)?.levelId ?? null;

// Success toast for a transactionCourseChange response; a change that went through with a
// failed bookkeeping step (CN / invoice / wallet / record) is surfaced as a warning instead.
export const reportChangeResult = (response: any, successMessage: string) => {
  const warnings: string[] = response?.warnings || [];
  console.info('[course change] trace', response?.traceId, response);
  if (warnings.length) {
    toast.warn(`Classes moved, but: ${warnings.join('; ')}. An alert was sent to Slack.${traceRef(response)}`, { autoClose: false });
  } else {
    toast.success(successMessage);
  }
};

// " (Ref: tcc_…)" — the backend trace id, so an admin can quote it when reporting a problem
// (it is in the Slack change-course log, CloudWatch and the transaction's courseChangeAudit).
export const traceRef = (data: any) => (data?.traceId ? ` (Ref: ${data.traceId})` : '');

// Error toast text for a failed transactionCourseChange call.
export const changeErrorMessage = (error: any, fallback: string) =>
  `${error?.response?.data?.error || fallback}${traceRef(error?.response?.data)}`;

// The classes moved but the follow-up changeLevel call failed.
export const reportActiveLevelFailure = (response: any, levelName: string) =>
  toast.warn(`Classes moved, but the active level (${levelName || '-'}) was not set. Set it from the Change Level tab.${traceRef(response)}`, { autoClose: false });

// Group transactions by course (first courseList line), newest transaction first in each
// group. Groups with remaining classes come first.
export const groupByCourse = (transactions: any[]) => {
  const groups: Record<string, { key: string; courseName: string; transactions: any[] }> = {};
  transactions.forEach((t) => {
    const first = t.courseList?.[0] || {};
    const key = String(first.courseId ?? first.courseName ?? 'unknown');
    if (!groups[key]) groups[key] = { key, courseName: first.courseName || 'Course', transactions: [] };
    groups[key].transactions.push(t);
  });
  const txnDate = (t: any) => new Date(t.invoice_date || t.createdAt || 0).getTime();
  return Object.values(groups)
    .map((g) => ({ ...g, transactions: [...g.transactions].sort((a, b) => txnDate(b) - txnDate(a)) }))
    .sort((a, b) => sumRemainingClasses({ courseList: b.transactions.flatMap((t) => t.courseList || []) }) -
      sumRemainingClasses({ courseList: a.transactions.flatMap((t) => t.courseList || []) }));
};

// One view over several selected transactions of the same course: the newest transaction's
// customer/currency fields with every transaction's course lines, so the remaining-class /
// value helpers and level prefills work across all of them.
export const combineTransactions = (transactions: any[]) => ({
  ...(transactions[0] || {}),
  courseList: transactions.flatMap((t) => t.courseList || []),
});

// fromCourse.removeLevels for the class move: remaining classes summed per level across the
// selected transactions.
export const buildRemoveLevels = (transactions: any[]) => {
  const byLevel: Record<string, any> = {};
  transactions.flatMap((t) => t.courseList || []).forEach((c: any) => {
    if (!(+c.eligibleRefundClasses > 0)) return;
    const key = String(c.level);
    if (!byLevel[key]) byLevel[key] = { level: c.level, levelId: c.levelId, courseName: c.courseName, noOfClasses: 0 };
    byLevel[key].noOfClasses += +c.eligibleRefundClasses;
  });
  return Object.values(byLevel);
};

// The `source` block of a transactionCourseChange payload: the selected transaction's
// remaining classes, its chain object and its Zoho item ids (all env variants).
export const buildSourcePayload = (transaction: any) => {
  const sourceCourseList = (transaction.courseList || [])
    .filter((c: any) => +c.eligibleRefundClasses > 0)
    .map((c: any) => ({
      courseId: c.courseId,
      courseName: c.courseName,
      level: c.level,
      levelId: c.levelId,
      item_id: c.item_id,
      item_id_prod: c.item_id_prod,
      item_id_uat: c.item_id_uat,
      account_id: c.account_id,
      account_id_prod: c.account_id_prod,
      account_id_uat: c.account_id_uat,
      unit_price: c.unit_price,
      classesToPurchase: +c.eligibleRefundClasses,
      discountedPrice: +c.refundDiscout || 0,
      isFree: !!c.isFree,
    }));
  return {
    id: transaction.__source === 'order' ? (transaction.order_id || transaction.id) : transaction.id,
    sourceTable: transaction.__source,
    paymentFor: transaction.paymentFor,
    courseChange: transaction.courseChange,
    invoice_result: transaction.invoice_result,
    customer_id: transaction.customer_id,
    email: transaction.email,
    name: transaction.name,
    currency: transaction.currency,
    centreId: transaction.centreId,
    place_of_supply: transaction.place_of_supply,
    courseList: sourceCourseList,
  };
};

// Build the priced "to course" detail (levelList with unit prices) for a paid course id.
// Used by every modal to render the destination course's levels + pricing.
export const buildToCourseDetail = async (
  paymentCourses: any[],
  courseId: string,
  paymentConfig: any,
  timezone: string,
) => {
  const toCourse = paymentCourses.find((c) => String(c.paidCourseId) === String(courseId));
  if (!toCourse) return null;

  const levelIds: number[] = [];
  const courseList = (toCourse.levels || []).map((lev: any) => {
    levelIds.push(Number(lev.levelId));
    return {
      courseId: toCourse.paidCourseId?.toString(),
      levelId: lev.levelId.toString(),
      courseName: toCourse.name,
      level: lev.name,
      classCount: 0, // admin enters the counts; not pre-filled with the package size
    };
  });

  const priceCCObj = paymentConfig?.timezoneWithCountryName?.[timezone];
  if (!priceCCObj) {
    throw new Error('Missing pricing region for this student');
  }
  const priceDetails = await getCoursePrice(levelIds, priceCCObj.code, priceCCObj.country.toLowerCase());
  const merged = courseList.map((course: any) => {
    const priceInfo = priceDetails?.data?.find((p: any) => p.levelId === Number(course.levelId));
    return {
      ...course,
      unit_price: priceInfo ? priceInfo.price : 0,
      currency: priceInfo ? priceInfo.currency : 'INR',
    };
  });
  return calculateTotalPrice({
    levelList: merged,
    currency: merged[0]?.currency || 'INR',
    discountPercentage: 0,
    discountedTotal: 0,
    total: 0,
  });
};
